import { useMemo } from "react";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import { dedupeByStableIdentity } from "./useLogIntegrityChecks";

/**
 * Per-player goal tally (PMA-COCK-145).
 *
 * Feeds the "En cancha" (on-pitch) list's goal-scored indicator
 * (`PlayerSelectorPanel.tsx`'s `renderPlayerGrid`): a soccer-ball icon next
 * to any player who has scored, with a small count badge when they've
 * scored more than once.
 *
 * A goal is `event.type === "Shot" && event.data?.outcome === "Goal"` — the
 * same predicate `useLiveScore.ts` already uses for the scoreboard, so this
 * hook stays in lockstep with what the app already treats as "a goal"
 * rather than re-deriving its own definition.
 *
 * Own goals are DELIBERATELY excluded from the scoring player's own tally.
 * `useActionFlow.ts`'s OwnGoal handler reassigns `team_id` to the opponent
 * (crediting the right team on the scoreboard) but leaves `player_id` as the
 * player who put it in their own net — showing a goal-ball on that player
 * here would misrepresent an own goal as a personal scoring contribution.
 *
 * Keyed by `player_id`, not by lineup slot or on-field status, so the tally
 * is stable across substitutions and a later re-entry — the count belongs
 * to the player, not to whatever position they currently occupy.
 */
export const useGoalTally = (
  liveEvents: MatchEvent[],
  queuedEvents: MatchEvent[],
): Record<string, number> =>
  useMemo(() => {
    const counts: Record<string, number> = {};
    // PMA-COCK-205 (EPIC-43): dedupeByStableIdentity guards the one
    // remaining by-design overlap (sendEvent()'s offline branch).
    for (const event of dedupeByStableIdentity([
      ...liveEvents,
      ...queuedEvents,
    ])) {
      if (event.type !== "Shot" || event.data?.outcome !== "Goal") continue;
      if (event.data?.own_goal === true) continue;
      const playerId = event.player_id;
      if (!playerId) continue;
      counts[playerId] = (counts[playerId] ?? 0) + 1;
    }
    return counts;
  }, [liveEvents, queuedEvents]);
